import React, { createContext, useState, useContext, useRef } from 'react';
import { ConfirmationToast } from '@components/ConfirmationToast';
import {
  ConfirmationToastContextProps,
  ConfirmationToastProps,
  ToastProviderProps,
} from '@helpers/Models';

const ConfirmationToastContext = createContext<
  ConfirmationToastContextProps | undefined
>(undefined);

export const ConfirmationToastProvider: React.FC<ToastProviderProps> = ({
  children,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastTitle, setToastTitle] = useState('');
  const [actionButtonName, setActionButtonName] = useState('');
  const [cancelButtonName, setCancelButtonName] = useState('');
  const [toastType, setToastType] =
    useState<ConfirmationToastProps['type']>('delete');
  const onActionRef = useRef<(() => void) | null>(null);
  const onCancelRef = useRef<(() => void) | null>(null);

  /**
   * Displays a confirmation toast with a message, title, and an optional callback function
   * to execute when the user confirms the action.
   *
   * @param {string} message - The message to display in the toast.
   * @param {string} title - The title of the toast.
   * @param {() => void} [onAction] - (Optional) A callback function that executes when the user confirms the action.
   *
   */
  const showConfirmationToast = (
    message: string,
    title: string,
    type: ConfirmationToastProps['type'],
    onAction?: () => void,
    buttonName?: string,
    onCancel?: () => void,
    cancelButtonName?: string,
  ) => {
    setToastMessage(message);
    setToastTitle(title);
    onActionRef.current = onAction || null;
    onCancelRef.current = onCancel || null;
    setShowToast(true);
    setToastType(type);
    setActionButtonName(buttonName || '');
    setCancelButtonName(cancelButtonName || '');

    // Reset first to force re-render even if toast is already open
    setShowToast(false);
    setTimeout(() => setShowToast(true), 0);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  return (
    <ConfirmationToastContext.Provider value={{ showConfirmationToast }}>
      {children}

      {showToast && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
          {/* Toast */}
          <div className="z-50 relative">
            <ConfirmationToast
              title={toastTitle}
              message={toastMessage}
              onClose={handleCloseToast}
              type={toastType}
              onAction={() => {
                if (onActionRef.current) {
                  onActionRef.current(); // Execute stored function
                }
                handleCloseToast(); // Close toast after execution
              }}
              buttonName={actionButtonName}
              onCancel={() => {
                if (onCancelRef.current) {
                  onCancelRef.current(); // Execute stored function
                }
              }}
              cancelButtonName={cancelButtonName}
            />
          </div>
        </>
      )}
    </ConfirmationToastContext.Provider>
  );
};

export const UseConfirmationToast = (): ConfirmationToastContextProps => {
  const context = useContext(ConfirmationToastContext);
  if (!context) {
    throw new Error(
      'ConfirmationToast must be used within a ConfirmationToastProvider',
    );
  }
  return context;
};
